import type { CalendarDate } from '@internationalized/date';
import type { Temporal } from 'temporal-polyfill';
import type { FormatLocale } from '#layers/base/public';
import { calendarDateConverter, formatPlainDate, nowDate, shortPlainDateFormatOptions } from '#layers/base/public';

/** Adapts an arbitrary range endpoint value to/from the calendar-date span the range picker operates on. */
export interface RangeValueAdapter<V> {
	/** Value → the calendar date used for span display and comparisons. */
	toCalendarDate: (value: V | null | undefined) => CalendarDate | null;
	/** A calendar-date selection → a value, preserving any non-date parts (e.g. time) of the previous value. */
	withDate: (previous: V | null | undefined, date: CalendarDate) => V;
	/** "Now" in the value's own type, for Set-to-Now. */
	now: () => V;
	/** Human-readable endpoint label for the trigger summary. */
	format: (value: V | null | undefined, locale: FormatLocale) => string;
}

/** Date-only adapter: the value IS a PlainDate, so date conversions are direct and time is irrelevant. */
export const plainDateRangeAdapter: RangeValueAdapter<Temporal.PlainDate> = {
	toCalendarDate: (value) => calendarDateConverter.toCalendar(value ?? null),
	withDate: (_previous, date) => calendarDateConverter.toTemporal(date)!,
	now: () => nowDate(),
	format: (value, locale) => formatPlainDate(value ?? null, locale, shortPlainDateFormatOptions) ?? '',
};
